import { useLocation, Link, Navigate } from "react-router-dom";
import { ArrowRight, MessageCircle, MapPin, Check } from "lucide-react";
import SEO from "../components/SEO";
import PricingProof from "../components/PricingProof";
import { serviceSchema, faqSchema } from "../data/schema";
import { Reveal, Chapter, MaskedLine, Stagger, Item } from "../components/Reveal";
import { openLeadPopup } from "../components/Navbar";
import { LOCATIONS, SERVICES } from "../data/content";
import { waLink, CONTEXT_MESSAGES } from "../lib/whatsapp";
import { track } from "../lib/analytics";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

export default function LocationPage() {
  const { pathname } = useLocation();
  const location = LOCATIONS.find((l) => l.slug === pathname.replace(/\/+$/, "").slice(1));
  if (!location) return <Navigate to="/" replace />;

  const featured = SERVICES.slice(0, 4);

  return (
    <main className="pt-36 lg:pt-44">
      <SEO
        title={`${location.heroTitle} | Grotech Web`}
        description={`${location.intro.slice(0, 150)}…`}
        path={`/${location.slug}`}
        schema={[
          serviceSchema({
            name: `Website Development in ${location.city}`,
            description: location.intro,
            path: `/${location.slug}`,
            serviceType: "Website development",
            areaServed: { "@type": "City", name: location.city },
          }),
          faqSchema(location.faqs),
        ]}
        breadcrumbs={[{ name: "Home", path: "/" }, { name: `Website Development ${location.city}`, path: `/${location.slug}` }]}
      />

      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16" data-testid="location-hero">
        <nav className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-white">Home</Link>
          <span className="mx-2 text-ember">/</span>
          <span className="text-white/70">Website Developer {location.city}</span>
        </nav>
        <h1 className="mt-8 max-w-4xl font-display text-4xl font-black leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl">
          <MaskedLine>{location.heroTitle}</MaskedLine>
        </h1>
        <Reveal hero delay={0.25}>
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-white/60 sm:text-lg">
            {location.intro}
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <button
              data-testid="location-primary-cta"
              onClick={() => { track("cta_click", { location: "location_page", city: location.city }); openLeadPopup(); }}
              className="group flex items-center gap-2 rounded-full bg-ember px-7 py-4 font-display text-sm font-bold uppercase tracking-wider transition-colors hover:bg-ember-hover"
            >
              Get a Free Website Plan
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <a
              href={waLink(`Hi Grotech Web! I'm based in ${location.city} and I'd like to discuss a website for my business.`)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="location-whatsapp-cta"
              onClick={() => track("whatsapp_click", { location: "location_page", city: location.city })}
              className="flex items-center gap-2 rounded-full border border-[#25D366]/50 px-7 py-4 font-display text-sm font-bold uppercase tracking-wider text-[#25D366] transition-colors hover:bg-[#25D366]/10"
            >
              <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
            </a>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16 lg:py-24">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Chapter index="01" label={`The ${location.city} Market`} />
            <Reveal delay={0.1}>
              <p className="mt-6 text-base leading-relaxed text-white/60">{location.market}</p>
              <div className="mt-8 flex items-start gap-3 rounded-2xl border border-white/10 bg-ink p-5">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-ember" />
                <p className="text-sm leading-relaxed text-white/60">
                  Based in New Delhi, working remotely with businesses across {location.city} and all
                  of India — clear process, weekly previews, direct communication.
                </p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Chapter index="02" label="What I Can Build for You" />
            <Stagger className="mt-8 grid gap-4 sm:grid-cols-2">
              {featured.map((s) => (
                <Item key={s.slug}>
                  <Link
                    to={`/${s.slug}/`}
                    className="group block h-full rounded-2xl border border-white/10 bg-ink p-6 transition-colors hover:border-ember/40"
                  >
                    <h2 className="font-display text-lg font-bold tracking-tight group-hover:text-ember">{s.title}</h2>
                    <p className="mt-2 text-sm leading-relaxed text-white/50">{s.short}</p>
                  </Link>
                </Item>
              ))}
            </Stagger>
          </div>
        </div>
      </section>

      <section className="bg-bone py-20 text-obsidian lg:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-8">
          <Chapter index="03" label={`Questions from ${location.city}`} dark />
          <div className="mt-8">
            <Accordion type="single" collapsible data-testid="location-faq">
              {location.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-obsidian/10">
                  <AccordionTrigger className="text-left font-display text-base font-semibold text-obsidian hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-obsidian/60">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
          <Reveal>
            <div className="mt-12 rounded-3xl bg-obsidian p-8 text-white sm:p-10">
              <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
                Your {location.city} business deserves a website that brings enquiries.
              </h2>
              <ul className="mt-6 space-y-3">
                {["Affordable premium build", "SEO foundation included", "Optional monthly growth plan"].map((f) => (
                  <li key={f} className="flex items-center gap-3 text-sm text-white/70">
                    <Check className="h-4 w-4 text-ember" /> {f}
                  </li>
                ))}
              </ul>
              <button
                data-testid="location-bottom-cta"
                onClick={openLeadPopup}
                className="mt-8 rounded-full bg-ember px-7 py-3.5 font-display text-xs font-bold uppercase tracking-wider transition-colors hover:bg-ember-hover"
              >
                Discuss My Business
              </button>
            </div>
          </Reveal>
        </div>
      </section>
      <PricingProof markets={["IN"]} heading={`Website pricing in ${location.city}`} />
    </main>
  );
}
